// Core packages

// Third party packages
import { FormlyFieldConfig } from "@ngx-formly/core";

// Custom packages
import { isFreeArchivePlan } from "src/app/modules/properties/document-archive-visibility.utils";
import {
  ARCHIVE_PAPER_SERVER_OWNED_DATA_KEYS,
  ARCHIVE_SERVER_OWNED_DATA_KEYS,
  buildPaperPartName,
  DocumentArchiveDocInsertSelection,
  DocumentArchiveDocPaperFile,
  DocumentArchiveDocPaperInsertData,
  PAPER_BARCODE_MAX_LENGTH,
  PAPER_BARCODE_PATTERN,
} from "src/app/shared/interfaces/document-archive-doc.interface";
import { DocumentTemplateTranslation } from "src/app/shared/interfaces/document-template.interface";

/**
 * Rules of the document insert on the 3.0.0 archive, behind the two insert pages
 * (`/properties/:propertyId/addNewDocumentsV3` and `/add-documentsV3`) through
 * DocumentArchiveInsertComponent.
 *
 * Plain functions over the choices of the page — the topics, sections, types and elements composed
 * out of `GET /document-archive-docs/insertable-types` by `buildInsertPageTopics`
 * (document-archive-insertable.utils.ts) —: no call, no Angular. The component owns WHAT is ticked,
 * these functions own the rules, so that the spec next to this file can pin them down.
 *
 * The flow is the one of the legacy insert pages (`property-add-new-documents`, `add-documents`): ONE
 * area at a time, any number of topics and sections inside it, document types of DIFFERENT sections in
 * the same insert as long as they share the data entry form, and the mapping elements chosen per
 * section. One insert is one document: one upload, one barcode, counted once.
 *
 * One legacy defect is deliberately NOT reproduced: outside the contracts the legacy enables the save
 * as soon as ONE element is ticked anywhere, and the back-end then skips in silence the types of every
 * section left without one. Here each section — each type, where the elements come from its own rows —
 * needs its own element. @see missingElements
 *
 * @since 3.0.0
 */

/**
 * One choice of the "Area" question: a GROUP of the taxonomy, numbered as the legacy insert pages
 * number it.
 *
 * @since 3.0.0
 */
export interface InsertAreaGroup {
  /** `group` of the areas of the tree */
  group: string;
  /** The legacy area code, which a paper insert writes as `data.area` and the module prints */
  code: string;
  /** Label of the radio button */
  labelKey: string;
}

/**
 * The four areas of the legacy insert pages, in their order.
 *
 * The codes are the ones the delivery note stores (`DocumentTypeStructure.getAreaFromChild2` in
 * `documentTypes.ts`), and the same table the archive page keeps for its own paper insert
 * (`PAPER_AREA_CODES` in `property-documents-v3.page.ts`): keep the two in step.
 */
export const INSERT_AREA_GROUPS: InsertAreaGroup[] = [
  {
    group: "amministrativo",
    code: "1",
    labelKey: "properties.addDocumentsPage.area.amministrativo",
  },
  {
    group: "contratti",
    code: "2",
    labelKey: "properties.addDocumentsPage.area.contratti",
  },
  {
    group: "tecnico",
    code: "3",
    labelKey: "properties.addDocumentsPage.area.tecnico",
  },
  {
    group: "unitaImmobiliari",
    code: "4",
    labelKey: "properties.addDocumentsPage.area.unitaImmobiliari",
  },
];

/**
 * Role types allowed to register a PAPER delivery: the back-end answers 403 to any other one, even
 * holding `canCreateDocuments`. Same list as `PAPER_INSERT_ROLE_TYPES` of the archive page.
 */
export const PAPER_INSERT_ROLE_TYPES = ["admin", "operator"];

/**
 * Role types allowed to file VIRTUAL documents (FE-M5): the ones the legacy insert page offers
 * «Virtuale» to, and the only ones the back-end lets create them (NEW-M44: 403 to any other role).
 */
export const VIRTUAL_INSERT_ROLE_TYPES = ["admin", "operator"];

/**
 * The fields a VIRTUAL document keeps as its form asks for them: the supplier — the name and the id
 * the supplier picker writes — and the date with its "not available" box. The legacy insert page
 * makes every other field optional on a virtual document. @see withVirtualRequiredFields
 */
export const VIRTUAL_REQUIRED_KEYS = [
  "ragioneSocialeFornitore",
  "ragioneSocialeFornitoreId",
  "dataDocumento",
  "dataDocumentochkNA",
];

/**
 * Role types the insert serves: the only ones `GET /insertable-types` answers, with a 403 to any other
 * even holding `canCreateDocuments`. Same list as `INSERT_ROLE_TYPES` of the archive page.
 */
export const INSERT_ROLE_TYPES = ["admin", "operator", "master", "collaborator"];

/**
 * A mapping element a document can be filed against.
 *
 * @since 3.0.0
 */
export interface InsertElementOption {
  /** Entry of `structureObjectIds` in the payload: the id of the mapping element */
  id: string;
  /** Name as the back-end resolved it; empty when it could not, and the template says so */
  label: string;
  selected: boolean;
  /**
   * True for «Edificio uso terziario» of the fire prevention section: not an element but the choice
   * of filing against none, in an entry of its own. Its id is never sent. @see buildInsertSelections
   */
  noElement?: boolean;
}

/**
 * Where the mapping elements of a section come from.
 *
 *  - `block`: the section is repeated per element (lifts, extinguishers, meters, property units…).
 *    Its elements in service are chosen once for the section and valid for every type ticked in it,
 *    as in the legacy;
 *  - `row`: each type offers ITS OWN elements: the contracts list, where a contract takes the elements
 *    of its own section — so a gas contract is never filed against an electricity meter —, and a
 *    section where a type serves a list of its own (the electricity supply contract);
 *  - `none`: the documents are filed against no element.
 */
export type InsertElementMode = "block" | "row" | "none";

/**
 * One document type of a section.
 *
 * @since 3.0.0
 */
export interface InsertDocTypeOption {
  /** `documentTemplateDocId` of the payload: the `_id` of the type in the taxonomy */
  id: string;
  label: string;
  /**
   * The help text the administrator wrote for the type, trimmed; EMPTY when there is none. Plain
   * text, to be rendered with `{{ }}` only, never as HTML (@see DocumentTemplateDoc.description)
   */
  description: string;
  /** Data entry form of the type; empty when it has none, and such types go together */
  dataTypeId: string;
  selected: boolean;
  /** False while the search box hides it */
  visible: boolean;
  /** Only in a `row` section: the elements this type is filed against */
  rowElements: InsertElementOption[];
}

/**
 * One section of a topic.
 *
 * @since 3.0.0
 */
export interface InsertSectionOption {
  /** `_id` of the section in the taxonomy: a list key, NOT always an ObjectId */
  id: string;
  label: string;
  checked: boolean;
  types: InsertDocTypeOption[];
  elementMode: InsertElementMode;
  /** `block` mode only: the elements of the section */
  blockElements: InsertElementOption[];
}

/**
 * One topic of the chosen area: an AREA of the taxonomy, what the legacy pages call "argomento".
 *
 * @since 3.0.0
 */
export interface InsertTopicOption {
  /** `_id` of the area */
  id: string;
  label: string;
  checked: boolean;
  sections: InsertSectionOption[];
}

/**
 * A place where an element still has to be ticked: a whole section in `block` mode, one type in
 * `row` mode.
 *
 * @since 3.0.0
 */
export interface InsertElementGap {
  sectionId: string;
  /** `null` when the gap is the whole section */
  typeId: string | null;
}

/**
 * What was filed, frozen when the insert succeeds: the summary on screen and the printed module read
 * it, and the selection behind it may be changed afterwards.
 *
 * @since 3.0.0
 */
export interface InsertSummary {
  /** Legacy code of the area, @see INSERT_AREA_GROUPS */
  areaCode: string;
  types: Array<{ id: string; label: string; sectionLabel: string }>;
  /** Names of the elements the document was filed against, without repetitions */
  elements: string[];
}

/**
 * The fields of a property the insert reads, out of `GET properties/:id`.
 *
 * @since 3.0.0
 */
export interface InsertPropertyData {
  _id?: string;
  name?: string;
  /** `false` on a suspended property, which the legacy warns about and inserts into all the same */
  active?: boolean;
  subscription?: { key?: string } | null;
  /** The mapping: an id, or the populated document */
  structure?: string | { _id?: string } | null;
  /** Progressive of the documents of the property, the base of the proposed paper barcode */
  totalDocsProgressive?: number | string | null;
  address?: { city?: string; route?: string; streetNumber?: string } | null;
}

/**
 * The data of `PrintQrDialogComponent`, as the legacy insert pages pass it.
 *
 * @since 3.0.0
 */
export interface InsertPrintDialogData {
  target: "module" | "qr";
  qrCodes: string[];
  propertyName: string;
  propertyCity: string;
  propertyAddress: string;
  /** Legacy area code, which the dialog translates */
  area: string;
  /** `key` is the dedup key of the dialog only: the type id here */
  docs: Array<{ title: string; key: string; subAreaTitle: string }>;
  /** Module only */
  uploadDate?: Date;
  /** Module only */
  content?: string;
  /** Module only, and left out when there is no element */
  structures?: Array<{ name: string }>;
}

/**
 * State of the mapping of the property, as far as the insert cares.
 *
 *  - `none`: nothing to check — no mapping, or a free plan, where the legacy does not block;
 *  - `unknown`: it had to be checked and could not be read.
 */
export type InsertMappingState = "none" | "closed" | "open" | "unknown";

/**
 * Localized label of a node of the tree: the language of the page, Italian when that one is missing.
 *
 * @since 3.0.0
 */
export function labelOf(
  translation: DocumentTemplateTranslation | null | undefined,
  lang: string
): string {
  if (!translation) {
    return "";
  }
  const localized = lang === "en" ? translation.en : translation.it;
  if (localized) {
    return localized;
  }
  return translation.it ? translation.it : "";
}

/**
 * Untick everything inside a section: what is filed is what the user picks AFTER ticking it again,
 * as in the legacy.
 *
 * @since 3.0.0
 */
function clearSection(section: InsertSectionOption): void {
  section.types.forEach((type) => {
    type.selected = false;
    type.rowElements.forEach((element) => (element.selected = false));
  });
  section.blockElements.forEach((element) => (element.selected = false));
}

/**
 * Tick or untick a topic. Unticking it unticks its sections and drops what was picked in them.
 *
 * @since 3.0.0
 */
export function setTopicChecked(topic: InsertTopicOption, checked: boolean): void {
  topic.checked = checked;
  if (checked) {
    return;
  }
  topic.sections.forEach((section) => {
    section.checked = false;
    clearSection(section);
  });
}

/**
 * Tick or untick a section. Unticking it drops what was picked in it.
 *
 * @since 3.0.0
 */
export function setSectionChecked(section: InsertSectionOption, checked: boolean): void {
  section.checked = checked;
  if (!checked) {
    clearSection(section);
  }
}

/**
 * The sections on screen: ticked, under a ticked topic.
 *
 * @since 3.0.0
 */
export function checkedSections(topics: InsertTopicOption[]): InsertSectionOption[] {
  const sections: InsertSectionOption[] = [];
  topics.forEach((topic) => {
    if (!topic.checked) {
      return;
    }
    topic.sections.forEach((section) => {
      if (section.checked) {
        sections.push(section);
      }
    });
  });
  return sections;
}

/**
 * The types being filed, in the order of the page.
 *
 * @since 3.0.0
 */
export function selectedTypes(topics: InsertTopicOption[]): InsertDocTypeOption[] {
  const types: InsertDocTypeOption[] = [];
  checkedSections(topics).forEach((section) => {
    section.types.forEach((type) => {
      if (type.selected) {
        types.push(type);
      }
    });
  });
  return types;
}

/**
 * The data entry form every selected type has to share, `null` while nothing is selected.
 *
 * One insert carries ONE set of values, so the first type picked decides the form for the others.
 * The types without a form share the empty one.
 *
 * @since 3.0.0
 */
export function lockedDataTypeOf(topics: InsertTopicOption[]): string | null {
  const selected = selectedTypes(topics);
  return selected.length ? selected[0].dataTypeId : null;
}

/**
 * Whether a type cannot join the insert because its form differs from the chosen one.
 *
 * @since 3.0.0
 */
export function isTypeLocked(type: InsertDocTypeOption, lockedDataTypeId: string | null): boolean {
  return lockedDataTypeId !== null && !type.selected && type.dataTypeId !== lockedDataTypeId;
}

/**
 * Apply the "Cerca il documento" box: a literal, case insensitive match on the name.
 *
 * A type the search hides LOSES its tick, as in the legacy: what is filed has to be what is on screen.
 * The text is never turned into a pattern, so any character can be typed.
 *
 * @since 3.0.0
 */
export function applySearch(topics: InsertTopicOption[], query: string): void {
  const needle = typeof query === "string" ? query.trim().toLowerCase() : "";
  topics.forEach((topic) => {
    topic.sections.forEach((section) => {
      section.types.forEach((type) => {
        type.visible = !needle || type.label.toLowerCase().indexOf(needle) > -1;
        if (!type.visible && type.selected) {
          type.selected = false;
          type.rowElements.forEach((element) => (element.selected = false));
        }
      });
    });
  });
}

/**
 * Where an element still has to be ticked before the form can open.
 *
 * Every section in `block` mode with a selected type needs one of its elements, and in `row` mode
 * every selected type that has elements to offer needs one of its own. A type with nothing to offer
 * is filed against no element, and so is a `none` section.
 *
 * @since 3.0.0
 */
export function missingElements(topics: InsertTopicOption[]): InsertElementGap[] {
  const gaps: InsertElementGap[] = [];
  checkedSections(topics).forEach((section) => {
    const picked = section.types.filter((type) => type.selected);
    if (!picked.length) {
      return;
    }
    if (section.elementMode === "block") {
      if (!section.blockElements.some((element) => element.selected)) {
        gaps.push({ sectionId: section.id, typeId: null });
      }
      return;
    }
    if (section.elementMode === "row") {
      picked.forEach((type) => {
        if (
          type.rowElements.length > 0 &&
          !type.rowElements.some((element) => element.selected)
        ) {
          gaps.push({ sectionId: section.id, typeId: type.id });
        }
      });
    }
  });
  return gaps;
}

/**
 * The elements a selected type of a section is filed against.
 *
 * @since 3.0.0
 */
function pickedElementsOf(
  section: InsertSectionOption,
  type: InsertDocTypeOption
): InsertElementOption[] {
  switch (section.elementMode) {
    case "block":
      return section.blockElements.filter((element) => element.selected);
    case "row":
      return type.rowElements.filter((element) => element.selected);
    default:
      return [];
  }
}

/**
 * The `docsSelected` of the insert: ONE entry per document type, with its own elements.
 *
 * The back-end writes one row per entry and per element and does not merge two entries naming the
 * same type, so a duplicate here would be a duplicated document. The same type can be ticked twice —
 * in its own section and in the aggregated contracts one — and then its elements are pooled.
 * `structureObjectIds` is left out when there is none, as the archive page does.
 *
 * «Edificio uso terziario» (`noElement`) is the one exception: a SECOND entry of the same type with
 * no element, one row on no element next to the rows of the ticked elements, as the legacy filed it.
 * The back-end refuses anything but an element id in `structureObjectIds`.
 *
 * @since 3.0.0
 */
export function buildInsertSelections(
  topics: InsertTopicOption[]
): DocumentArchiveDocInsertSelection[] {
  const order: string[] = [];
  const elementsByType: { [typeId: string]: string[] } = {};
  const withoutElement: { [typeId: string]: boolean } = {};

  checkedSections(topics).forEach((section) => {
    section.types.forEach((type) => {
      if (!type.selected) {
        return;
      }
      if (!elementsByType[type.id]) {
        elementsByType[type.id] = [];
        order.push(type.id);
      }
      const known = elementsByType[type.id];
      pickedElementsOf(section, type).forEach((element) => {
        if (element.noElement) {
          withoutElement[type.id] = true;
          return;
        }
        if (known.indexOf(element.id) === -1) {
          known.push(element.id);
        }
      });
    });
  });

  const selections: DocumentArchiveDocInsertSelection[] = [];
  order.forEach((typeId) => {
    const ids = elementsByType[typeId];
    if (ids.length) {
      selections.push({ documentTemplateDocId: typeId, structureObjectIds: ids });
    }
    if (!ids.length || withoutElement[typeId]) {
      selections.push({ documentTemplateDocId: typeId });
    }
  });
  return selections;
}

/**
 * Whether archiving from a row of the dossier FILLS that very row instead of filing a new one next
 * to it: what the "+" of the row may promise.
 *
 * The "+" names the row to the back-end (`fillDocId`, EXT-21), which fills it while it is still
 * waiting for its document: not expired, not deleted, not a mockup, and with no code yet — the
 * `present` the back-end computes, never recomputed here. WITH OR WITHOUT `data`: a red row already
 * carrying part of the form — the tenant notice opened with the tenant's name, legacy AJ4 — is filled
 * too, and keeps what it carries. @see archiveFillRowOf
 *
 * NOT the colour of the row. On the free plans no obligation is red, yet their waiting rows are
 * filled all the same.
 *
 * `present` has to be `false`, not merely falsy: a row that does not say is not promised, and the
 * insert files beside it as before — the safe side, since naming a row already filed is refused.
 *
 * @since 3.0.0
 *
 * @param row The row the insert starts from
 */
export function isArchiveRowFillable(
  row: { expired?: boolean; deleted?: boolean; mockup?: boolean; present?: boolean } | null | undefined
): boolean {
  return (
    !!row &&
    row.expired !== true &&
    row.deleted !== true &&
    row.mockup !== true &&
    row.present === false
  );
}

/**
 * The row the "+" of the dossier FILLS, as the insert dialog needs it (EXT-21, FEX-26).
 *
 * @since 3.0.0
 */
export interface ArchiveFillRow {
  /** Sent as `fillDocId` */
  _id: string;
  /** The type and the element of the row, which decide whether the insert still files on it */
  documentTemplateDocId: string;
  /** `""` for a row hanging from no element */
  structureObjectId: string;
  /** What the row carries and keeps once filled. @see archiveRowKeptData */
  data: { [key: string]: unknown };
}

/** `data.reason` of the 409 of a row that can no longer be filled (EXT-21, asked with FEX-26) */
export const ARCHIVE_FILL_ROW_NOT_FILLABLE = "fill_row_not_fillable";

/**
 * What a row keeps when it is filled: its values, without the keys the server owns — the code, the
 * files, who filed it and when, and the parts of a paper document — which the insert writes afresh.
 * The back-end makes the same cut (`fillableRowData`) before comparing the form with the row.
 *
 * A `data` that is not an object keeps nothing. `__proto__` is skipped, so that a stored key can never
 * reach the prototype of the copy.
 *
 * @since 3.0.0
 *
 * @param data The `data` of the row, as the tree serves it
 */
export function archiveRowKeptData(data: unknown): { [key: string]: unknown } {
  const kept: { [key: string]: unknown } = {};
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return kept;
  }
  const serverKeys = ARCHIVE_SERVER_OWNED_DATA_KEYS.concat(ARCHIVE_PAPER_SERVER_OWNED_DATA_KEYS);
  const values = data as { [key: string]: unknown };
  Object.keys(values).forEach((key) => {
    if (key === "__proto__" || serverKeys.indexOf(key) > -1) {
      return;
    }
    kept[key] = values[key];
  });
  return kept;
}

/**
 * The row to fill out of a row of the tree, or `undefined` when its "+" files a new row beside it.
 * @see isArchiveRowFillable
 *
 * @since 3.0.0
 *
 * @param row The row the insert starts from
 */
export function archiveFillRowOf(
  row:
    | {
        _id?: string;
        documentTemplateDocId?: string;
        structureObjectId?: string;
        expired?: boolean;
        deleted?: boolean;
        mockup?: boolean;
        present?: boolean;
        data?: unknown;
      }
    | null
    | undefined
): ArchiveFillRow | undefined {
  if (!isArchiveRowFillable(row) || !row._id || !row.documentTemplateDocId) {
    return undefined;
  }
  return {
    _id: row._id,
    documentTemplateDocId: row.documentTemplateDocId,
    structureObjectId: row.structureObjectId ? row.structureObjectId : "",
    data: archiveRowKeptData(row.data),
  };
}

/**
 * An id as the back-end compares it when it matches the row to fill: lower case, `""` for none.
 *
 * @since 3.0.0
 */
function comparableFillId(value: unknown): string {
  return value === null || value === undefined || value === "" ? "" : String(value).toLowerCase();
}

/**
 * Whether an insert still files on the row it was started from, so that naming that row
 * (`fillDocId`) is right: the type of the row is among the types sent, with the element of the row
 * among ITS elements — or sent without elements, for a row hanging from none.
 *
 * The rule of the back-end (`matchFillPair`, where a type sent without elements, or with an empty
 * list, files on no element), mirrored so that the row is named only when it will be accepted. The
 * user may have moved the document to another element: the insert is then a new row there, and the
 * row of the "+" keeps waiting.
 *
 * @since 3.0.0
 *
 * @param selections The types the insert sends, with their elements
 * @param fillRow    The row the "+" was clicked on
 */
export function isFillRowSelected(
  selections: DocumentArchiveDocInsertSelection[] | null | undefined,
  fillRow: ArchiveFillRow | null | undefined
): boolean {
  if (!fillRow || !Array.isArray(selections)) {
    return false;
  }
  const typeId = comparableFillId(fillRow.documentTemplateDocId);
  if (!typeId) {
    return false;
  }
  const elementId = comparableFillId(fillRow.structureObjectId);
  return selections.some((selection) => {
    if (!selection || comparableFillId(selection.documentTemplateDocId) !== typeId) {
      return false;
    }
    const ids =
      Array.isArray(selection.structureObjectIds) && selection.structureObjectIds.length
        ? selection.structureObjectIds
        : [""];
    return ids.some((id) => comparableFillId(id) === elementId);
  });
}

/**
 * Lock the fields whose values the row being filled already carries: they arrive filled in and
 * cannot be changed, since the back-end refuses an insert that changes — or empties — a value of the
 * row (`fillDataConflicts`): the tenant's name on a tenant notice, say.
 *
 * Read only and disabled, like the read only forms of the builder (`DataTypePreviewBuilder`). The
 * expressions that would unlock the field on the next pass go — the `disabled` the builder flips on
 * a date while its «not available» box changes, any `readonly` —, the others stay: `required` only
 * draws the asterisk on a disabled control, which is out of the validation, and a derivation
 * (`onChange`) from a value that cannot change is harmless.
 *
 * Every key the row carries is locked, an empty value included: that too is a value the back-end
 * keeps.
 *
 * @since 3.0.0
 *
 * @param fields  The form, as built
 * @param rowData What the row keeps. @see archiveRowKeptData
 * @returns The keys locked, in the order of the form
 */
export function lockFilledFields(
  fields: FormlyFieldConfig[] | null | undefined,
  rowData: { [key: string]: unknown } | null | undefined
): string[] {
  const locked: string[] = [];
  if (!rowData) {
    return locked;
  }
  const walk = (list: FormlyFieldConfig[] | null | undefined): void => {
    (Array.isArray(list) ? list : []).forEach((field) => {
      if (!field) {
        return;
      }
      const key = field.key;
      if (
        typeof key === "string" &&
        Object.prototype.hasOwnProperty.call(rowData, key) &&
        locked.indexOf(key) === -1
      ) {
        const expressions = field.expressionProperties;
        if (expressions) {
          Object.keys(expressions).forEach((path) => {
            if (/\.(disabled|readonly)$/.test(path)) {
              delete expressions[path];
            }
          });
        }
        field.templateOptions = {
          ...(field.templateOptions ? field.templateOptions : {}),
          readonly: true,
          disabled: true,
        };
        locked.push(key);
      }
      walk(field.fieldGroup);
    });
  };
  walk(fields);
  return locked;
}

/**
 * The values of an insert that FILLS a row: the locked ones, which the row already carries, are
 * left out, and the back-end keeps them as stored (`fillDataConflicts` compares only what is sent).
 * Sending them back could only ever be refused — a date a picker rewrote, a value a widget
 * reshaped — and would never change anything.
 *
 * Except the keys the endpoint cannot do without — the content of a paper delivery note — which
 * travel with the row's own value, the stored one.
 *
 * @since 3.0.0
 *
 * @param values     The values of the form
 * @param lockedKeys The keys locked. @see lockFilledFields
 * @param rowData    What the row keeps
 * @param required   Keys the endpoint requires
 */
export function fillInsertData(
  values: { [key: string]: unknown },
  lockedKeys: string[],
  rowData: { [key: string]: unknown },
  required: string[] = []
): { [key: string]: unknown } {
  const data: { [key: string]: unknown } = {};
  Object.keys(values ? values : {}).forEach((key) => {
    if (lockedKeys.indexOf(key) === -1) {
      data[key] = values[key];
    }
  });
  required.forEach((key) => {
    if (lockedKeys.indexOf(key) > -1 && rowData && Object.prototype.hasOwnProperty.call(rowData, key)) {
      data[key] = rowData[key];
    }
  });
  return data;
}

/**
 * Whether a failed insert is the 409 of the row it was filling: filed, deleted or expired since the
 * dossier was read, by somebody else most likely. Nothing was written — a race lost against another
 * filing consumes a progressive and nothing more — and the form cannot fix it: the answer is to read
 * the dossier again.
 *
 * Only an insert that named a row can get it. The back-end marks it with
 * `data.reason = "fill_row_not_fillable"`; until it does, the digital insert tells it apart all the
 * same — the row's is the only 409 it answers — while the paper one keeps reading a bare 409 as a
 * barcode already filed, the other 409 it answers.
 *
 * @since 3.0.0
 *
 * @param err      The error of the call
 * @param mode     Which insert failed
 * @param fillSent Whether the insert named a row
 */
export function isArchiveFillRowConflict(
  err: unknown,
  mode: "digital" | "paper",
  fillSent: boolean
): boolean {
  if (!fillSent || !err || typeof err !== "object") {
    return false;
  }
  const failure = err as { status?: unknown; error?: unknown };
  if (failure.status !== 409) {
    return false;
  }
  const body = failure.error as { data?: unknown } | null | undefined;
  const data = body && typeof body === "object" ? (body.data as { reason?: unknown }) : undefined;
  if (data && typeof data === "object" && data.reason === ARCHIVE_FILL_ROW_NOT_FILLABLE) {
    return true;
  }
  return mode === "digital";
}

/**
 * The legacy code of an area, `""` for a group the four do not know — which makes the paper branch
 * refuse to send rather than write something undecodable on the delivery note.
 *
 * @since 3.0.0
 */
export function paperAreaCodeOf(group: string): string {
  const match = INSERT_AREA_GROUPS.filter((item) => item.group === group)[0];
  return match ? match.code : "";
}

/**
 * The barcode to propose for the next paper delivery, composed as the legacy and the archive page
 * compose it: property id, "S", the progressive plus one padded to six digits, "C".
 *
 * A suggestion only: the code that counts is the one printed on the label. `""` when it cannot be
 * composed — no property, no counter, or a counter past six digits that padding cannot represent.
 *
 * @since 3.0.0
 */
export function suggestedPaperBarCode(propertyId: string, totalDocsProgressive: unknown): string {
  if (!propertyId) {
    return "";
  }
  const progressive = Number(totalDocsProgressive);
  if (isNaN(progressive)) {
    return "";
  }
  const next = (progressive + 1).toString();
  if (next.length > 6) {
    return "";
  }
  return `${propertyId}S${next.padStart(6, "0")}C`.toUpperCase();
}

/**
 * The mapping id of a property, whether the field arrives as an id or as a populated document.
 *
 * @since 3.0.0
 */
export function structureIdOf(property: InsertPropertyData | null | undefined): string {
  const structure = property ? property.structure : undefined;
  if (!structure) {
    return "";
  }
  if (typeof structure === "string") {
    return structure;
  }
  return structure._id ? String(structure._id) : "";
}

/**
 * Whether the state of the mapping has to be read before inserting.
 *
 * Only with a mapping, and never on the free plans: there the legacy inserts with the mapping open.
 *
 * @since 3.0.0
 */
export function needsMappingCheck(subscriptionKey: string, structureId: string): boolean {
  return !isFreeArchivePlan(subscriptionKey) && !!structureId;
}

/**
 * The state of the mapping out of `GET condo-structures/:id`. Anything but a readable answer is
 * `unknown`, and blocks the insert. @see mappingBlocksInsert
 *
 * @since 3.0.0
 */
export function mappingStateOf(res: unknown): InsertMappingState {
  // Whatever arrives, including the Error the ApiService emits when the network is down
  const answer = res as { status?: unknown; data?: { open?: unknown } } | null;
  if (!answer || !answer.status || !answer.data) {
    return "unknown";
  }
  return answer.data.open ? "open" : "closed";
}

/**
 * Whether the mapping keeps the insert closed: open, as the legacy rules, or impossible to read —
 * the legacy page is stuck in that case, and inserting blind would defeat the rule.
 *
 * @since 3.0.0
 */
export function mappingBlocksInsert(state: InsertMappingState): boolean {
  return state === "open" || state === "unknown";
}

/**
 * Freeze what is being filed, for the summary and the printed module.
 *
 * @since 3.0.0
 */
export function buildInsertSummary(topics: InsertTopicOption[], group: string): InsertSummary {
  const types: Array<{ id: string; label: string; sectionLabel: string }> = [];
  const elements: string[] = [];

  checkedSections(topics).forEach((section) => {
    section.types.forEach((type) => {
      if (!type.selected) {
        return;
      }
      if (!types.some((item) => item.id === type.id)) {
        types.push({ id: type.id, label: type.label, sectionLabel: section.label });
      }
      pickedElementsOf(section, type).forEach((element) => {
        if (element.label && elements.indexOf(element.label) === -1) {
          elements.push(element.label);
        }
      });
    });
  });

  return { areaCode: paperAreaCodeOf(group), types, elements };
}

/**
 * What the QR labels carry: the parts of a paper delivery, or the barcode alone — for a digital
 * document, and for a paper one declared in no part, as the legacy prints it.
 *
 * @since 3.0.0
 */
export function printQrCodes(paper: boolean, barCode: string, partNames: string[]): string[] {
  const parts = Array.isArray(partNames) ? partNames.filter((name) => !!name) : [];
  if (paper && parts.length) {
    return parts;
  }
  return barCode ? [barCode] : [];
}

/**
 * The data of `PrintQrDialogComponent`, in the shape the legacy insert pages give it.
 *
 * `key` of a document is only the dedup key of the dialog: the type id, never the retired legacy key.
 * `structures` stays undefined when there is no element, as in the legacy.
 *
 * @since 3.0.0
 */
export function buildPrintDialogData(
  target: "module" | "qr",
  qrCodes: string[],
  property: InsertPropertyData | null,
  summary: InsertSummary,
  content: string,
  uploadDate: Date
): InsertPrintDialogData {
  const address: { city?: string; route?: string; streetNumber?: string } =
    property && property.address ? property.address : {};
  const route = address.route ? address.route : "";
  const streetNumber = address.streetNumber ? address.streetNumber : "";
  const propertyAddress = route && streetNumber ? `${route}, ${streetNumber}` : route || streetNumber;
  const docs = summary.types.map((type) => ({
    title: type.label,
    key: type.id,
    subAreaTitle: type.sectionLabel,
  }));

  const data: InsertPrintDialogData = {
    target,
    qrCodes,
    propertyName: property && property.name ? property.name : "",
    propertyCity: address.city ? address.city : "",
    propertyAddress,
    area: summary.areaCode,
    docs,
  };

  if (target === "module") {
    data.uploadDate = uploadDate;
    data.content = content;
    data.structures = summary.elements.length
      ? summary.elements.map((name) => ({ name }))
      : undefined;
  }

  return data;
}

/*
 * THE VALUES OF THE DOCUMENT, filled in on the page as the legacy insert pages do: the form of the
 * data type, then the upload of a digital document or the files of a paper delivery.
 */

/** Uploads one digital insert can name: the limit of the back-end (`MAX_INSERT_FILES`) */
export const DIGITAL_MAX_FILES = 20;

/**
 * Files one paper delivery can be split into, as the back-end counts the parts of a delivery. The
 * legacy page took up to 100: the new archive refuses more than this.
 */
export const PAPER_MAX_FILES = 20;

/** The smallest list "Numero di file" builds, as in the legacy: one file is the barcode itself */
export const PAPER_MIN_FILES = 2;

/** The key the file uploader writes into: a payload field of its own, never a value of `data` */
export const INSERT_FILE_KEY = "documentFile";

/** The data entry key the paper insert cannot do without: the content of the delivery note */
export const PAPER_CONTENT_KEY = "contenutoDocumento";

/** Type of a new file row: "Relazione", the default of the legacy radios */
export const PAPER_DEFAULT_FILE_DOC_TYPE = "report";

/**
 * The five kinds of file of a paper delivery, as the legacy radios name them, with the letter each
 * one puts in the file name: the letters of `PAPER_PART_TYPES`, already spelled inside the names of
 * the files on record, so they are data and never change.
 */
export const PAPER_FILE_DOC_TYPES: Array<{ value: string; letter: string; labelKey: string }> = [
  { value: "report", letter: "R", labelKey: "properties.detail.documentArchive.fields.report" },
  { value: "tables", letter: "T", labelKey: "properties.detail.documentArchive.fields.tables" },
  { value: "calcs", letter: "C", labelKey: "properties.detail.documentArchive.fields.calcs" },
  { value: "schemas", letter: "S", labelKey: "properties.detail.documentArchive.fields.schemas" },
  { value: "various", letter: "V", labelKey: "properties.detail.documentArchive.fields.various" },
];

/**
 * The letter a file type puts in the file name. "R" when the type is unknown, the default type.
 *
 * @since 3.0.0
 */
export function paperFileLetter(docType: string | null | undefined): string {
  const match = PAPER_FILE_DOC_TYPES.filter((item) => item.value === docType)[0];
  return match ? match.letter : "R";
}

/**
 * Whether "Numero di file" holds something the page can file: empty — the document is ONE file, named
 * after the barcode — or a whole number from 2 to the limit.
 *
 * @since 3.0.0
 */
export function isPaperNumFilesValid(numFiles: unknown): boolean {
  if (numFiles === null || numFiles === undefined || numFiles === "") {
    return true;
  }
  const value = Number(numFiles);
  return (
    isFinite(value) &&
    Math.floor(value) === value &&
    value >= PAPER_MIN_FILES &&
    value <= PAPER_MAX_FILES
  );
}

/**
 * How many file rows "Numero di file" asks for: none while it is empty or not a whole number of at
 * least two, and never more than the limit — a larger number keeps the limit on screen, and the
 * field says why it cannot be saved. Unlike the legacy hook, a negative or decimal value cannot
 * break the list.
 *
 * @since 3.0.0
 */
export function paperFileRowsCount(numFiles: unknown): number {
  if (numFiles === null || numFiles === undefined || numFiles === "") {
    return 0;
  }
  const value = Number(numFiles);
  if (!isFinite(value) || Math.floor(value) !== value || value < PAPER_MIN_FILES) {
    return 0;
  }
  return Math.min(value, PAPER_MAX_FILES);
}

/**
 * The barcode as it is sent: trimmed and uppercased, as the back-end stores it.
 *
 * @since 3.0.0
 */
export function normalizedBarCode(raw: unknown): string {
  return typeof raw === "string" ? raw.trim().toUpperCase() : "";
}

/**
 * What is wrong with a barcode, `null` when nothing — an empty one is for `required` to say. The rule
 * of the back-end, on the uppercased value: anything ending with "S", six digits and "C".
 *
 * @since 3.0.0
 */
export function paperBarCodeError(raw: unknown): "barCodeShape" | "barCodeTooLong" | null {
  const code = normalizedBarCode(raw);
  if (!code) {
    return null;
  }
  if (code.length > PAPER_BARCODE_MAX_LENGTH) {
    return "barCodeTooLong";
  }
  return PAPER_BARCODE_PATTERN.test(code) ? null : "barCodeShape";
}

/**
 * The names of the files of a paper delivery, exactly as the QR labels print them and the back-end
 * stores them: `<barcode>_<letter><n>`, n counting across the whole document from 1, or the bare
 * barcode for a document in one file. DERIVED, never typed: a new barcode or a new type renames them.
 *
 * @since 3.0.0
 *
 * @param barCode  The barcode in the field, as typed
 * @param docTypes The type of each file row; none for a document in one file
 */
export function paperFileNames(barCode: string, docTypes: Array<string | null | undefined>): string[] {
  const code = normalizedBarCode(barCode);
  if (!docTypes.length) {
    return code ? [code] : [];
  }
  return docTypes.map((docType, index) =>
    buildPaperPartName(code, paperFileLetter(docType), index, docTypes.length)
  );
}

/**
 * The values of the form as the `data` of an insert: without the uploader key, which travels as a
 * payload field of its own, and without the keys the back-end owns.
 *
 * @since 3.0.0
 */
export function insertDataOf(
  values: { [key: string]: unknown } | null | undefined
): { [key: string]: unknown } {
  const data: { [key: string]: unknown } = {};
  if (!values) {
    return data;
  }
  Object.keys(values).forEach((key) => {
    if (key === INSERT_FILE_KEY || ARCHIVE_SERVER_OWNED_DATA_KEYS.indexOf(key) > -1) {
      return;
    }
    data[key] = values[key];
  });
  return data;
}

/**
 * The `data` of a paper insert: the values of the form, plus the keys the endpoint demands.
 *
 * Those keys are written LAST and win over a data type field of the same name: on a paper insert
 * `code`, `files`, `numFiles` and `area` belong to the delivery. `numFiles` is the length of the list
 * and never typed: the back-end refuses a payload where the two differ, and the invoicing reads it.
 * Every file starts `missing`: there are no bytes yet, and that is what puts it on the checklist of
 * the scanning team.
 *
 * @since 3.0.0
 */
export function buildPaperInsertData(
  values: { [key: string]: unknown } | null | undefined,
  barCode: string,
  fileNames: string[],
  area: string
): DocumentArchiveDocPaperInsertData {
  const files: DocumentArchiveDocPaperFile[] = fileNames.map((name) => ({
    name,
    status: "missing" as "missing",
  }));
  const rest = insertDataOf(values);
  // Required by the form of a paper insert (@see withRequiredContent), and by the back-end
  const content = rest[PAPER_CONTENT_KEY];
  return {
    ...rest,
    contenutoDocumento: typeof content === "string" ? content : "",
    code: normalizedBarCode(barCode),
    files,
    numFiles: files.length,
    area: area ? area.trim() : "",
  };
}

/**
 * The field carrying a given key, at any depth of a built form.
 *
 * @since 3.0.0
 */
export function findFieldByKey(
  fields: FormlyFieldConfig[] | null | undefined,
  key: string
): FormlyFieldConfig | null {
  const list = Array.isArray(fields) ? fields : [];
  for (let i = 0; i < list.length; i++) {
    const field = list[i];
    if (!field) {
      continue;
    }
    if (field.key === key) {
      return field;
    }
    const nested = findFieldByKey(field.fieldGroup, key);
    if (nested) {
      return nested;
    }
  }
  return null;
}

/**
 * Make sure a paper form asks for `contenutoDocumento`, which the paper endpoint requires — the same
 * rule as the insert dialog of the archive page.
 *
 * Normally the field is already there, one of the three common fields of every document form, and it
 * is only forced to `required`, dropping a `requiredWhen` expression that would switch it back off.
 * For a type with no form at all it is appended.
 *
 * @since 3.0.0
 *
 * @param fields The form of the data type, as the builder produced it
 * @param label  The translated label of the appended field
 */
export function withRequiredContent(
  fields: FormlyFieldConfig[],
  label: string
): FormlyFieldConfig[] {
  const existing = findFieldByKey(fields, PAPER_CONTENT_KEY);
  if (existing) {
    if (!existing.templateOptions) {
      existing.templateOptions = {};
    }
    existing.templateOptions.required = true;
    if (existing.expressionProperties) {
      delete existing.expressionProperties["templateOptions.required"];
    }
    return fields;
  }

  return fields.concat([
    {
      fieldGroupClassName: "row",
      fieldGroup: [
        {
          className: "col-12",
          type: "textarea",
          key: PAPER_CONTENT_KEY,
          templateOptions: {
            label,
            placeholder: label,
            required: true,
            // The back-end refuses anything longer, whatever the field it sits in
            maxLength: 5000,
            rows: 3,
          },
        },
      ],
    },
  ]);
}

/**
 * The form of a VIRTUAL document (FE-M5), as the legacy insert page builds it: no field is required
 * but the supplier and the date, which keep what the form says about them (@see VIRTUAL_REQUIRED_KEYS).
 * The back-end requires no field of a virtual document, so the page is the only one asking.
 *
 * Every other field loses `required` AND the `requiredWhen` expression that would switch it back on;
 * its other expressions stay. The date keeps its own, which is what lets "not available" stand in for
 * it. At any depth of the built form. Changed in place, as @see withRequiredContent does: the fields
 * are built for this form alone.
 *
 * @since 3.0.0
 *
 * @param fields The form of the data type, as the builder produced it
 */
export function withVirtualRequiredFields(fields: FormlyFieldConfig[]): FormlyFieldConfig[] {
  const list = Array.isArray(fields) ? fields : [];
  list.forEach((field) => {
    if (!field) {
      return;
    }
    if (Array.isArray(field.fieldGroup)) {
      withVirtualRequiredFields(field.fieldGroup);
    }
    if (typeof field.key !== "string" || VIRTUAL_REQUIRED_KEYS.indexOf(field.key) > -1) {
      return;
    }
    if (field.templateOptions) {
      field.templateOptions.required = false;
    }
    if (field.expressionProperties) {
      delete field.expressionProperties["templateOptions.required"];
    }
  });
  return fields;
}
